// common
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useState,
} from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

// style
import styles from './style';
import Button from '../button';
import { debounce } from '../../helpers/debounce';

// Props Interface
export interface TextInputFieldProps {
  name?: string;
  className?: string;
  inputClassName?: string;
  placeholder?: any;
  value?: any;
  secure?: boolean;
  submitBtnClassName?: string;
  submitBtnColor?: string;
  submitBtnText?: string;
  submitBtnTextColor?: string;
  onSubmit?: () => void;
  onFocus?: () => any;
  onBlur?: (e?: any) => any;
  onChangeText?: any;
  onChangeEvent?: any;
  onPaste?: (e) => any;
  isInputFocused?: boolean;

  bottomLine?: boolean;

  type?: any;
  bordered?: boolean;
  borderColor?: string;
  width?: number | string;
  height?: number | string;
  textAlign?: any;
  unit?: any;
  descriptionClassName?: string;
  description?: string;
  defaultValue?: any;
  disabled?: boolean;
  textContentType?: string | undefined;
  numbersOnly?: boolean;

  inputStyle?: any;
  inputContainerStyle?: any;

  mask?: any;
  margin?: number | any;
  marginBottom?: number | any;
  marginRight?: number | any;
  maxLength?: number | any;

  right?: any;
  icon?: any;
  button?: any;
  calendar?: any;

  register?: UseFormRegisterReturn<any>;
  autoResize?
}

const TextInputField: ForwardRefExoticComponent<
  TextInputFieldProps & RefAttributes<HTMLInputElement>
> = forwardRef<HTMLInputElement, TextInputFieldProps>((props, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const onBlur = () => {
    debounce(() => {
      setIsFocused(false);
      props.onBlur ? props.onBlur() : null;
    }, 300);
  };

  const onFocus = () => {
    debounce(() => {
      setIsFocused(true);
      props.onFocus ? props.onFocus() : null;
    }, 200);
  };

  const onPaste = (e: any) => {
    props.onPaste ? props.onPaste(e) : null;
  };

  const margin = props.margin !== undefined ? { margin: props.margin } : null;
  const marginBottom =
    props.marginBottom !== undefined
      ? { marginBottom: props.marginBottom }
      : styles.defaultMarginBottom;
  const marginRight = props.marginRight
    ? { marginRight: props.marginRight }
    : {};

  const bordered = props.bordered ? styles.inputFieldBorder : null;
  const borderColor = props.borderColor
    ? { borderColor: props.borderColor }
    : {};
  const width = props.width ? { width: props.width } : {};
  const height = props.height ? { height: props.height } : {};
  const textAlign = props.textAlign ? { textAlign: props.textAlign } : null;

  const bottomLine =
    props.bottomLine === false ? null : styles.inputFieldBottomLine;

  let iconStyles = {};
  switch (props.icon?.position) {
    case 'left':
      iconStyles = styles.leftIcon;
      break;
    case 'right':
      iconStyles = styles.rightIcon;
      break;
  }

  const renderIcons = () => {
    return <div style={iconStyles}>{props.icon?.icon}</div>;
  };

  let buttonStyles = {};
  switch (props.button?.position) {
    case 'left':
      iconStyles = styles.leftIcon;
      break;
    case 'right':
      iconStyles = styles.rightIcon;
      break;
  }

  const renderButtons = () => {
    if (props.button.condition === 'on-focus') {
      if (!isFocused) return null;
    }

    return props.button ? props.button.button() : null;
  };

  const renderDescription = () => {
    if (!props.description) {
      return null;
    }

    return (
      <span
        className={props.descriptionClassName ?? ''}
        style={props.descriptionClassName ? {} : styles.descriptionText}
      >
        {props.description}
      </span>
    );
  };

  const submitBtn = () => {
    if (!props.submitBtnText) {
      return null;
    }

    return (
      <>
        <div style={styles.submitButtonContainer}>
          <Button
            buttonColor={props.submitBtnColor}
            textColor={props.submitBtnTextColor}
            className={props.submitBtnClassName ?? ''}
            text={props.submitBtnText}
          />
        </div>
      </>
    );
  };

  const renderUnit = () => {
    if (!props.unit) {
      return null;
    }

    return (
      <div style={styles.unitContainer}>
        <span
          style={{
            ...styles.unitText,
            ...styles.inputField,
            ...textAlign,
          }}
        >
          {props.unit}
        </span>
      </div>
    );
  };

  return (
    <div
      style={styles.container}
      className={`${props.className ? props.className : ''}`}
    >
      <div
        style={{
          ...styles.inputFieldContainer,
          ...width,
          ...height,
          ...margin,
          ...marginBottom,
          ...marginRight,
          ...bottomLine,
          ...bordered,
          ...borderColor,
          ...props.inputContainerStyle,
        }}
      >
        <div style={styles.inner}>
          {props.icon?.position === 'left' ? renderIcons() : null}
          {props.button?.position === 'left' ? renderButtons() : null}
          {props.icon?.position === 'centerLeft' ? renderIcons() : null}

          {props.mask ? (
            <input
              ref={ref}
              name={props.name ? props.name : null}
              className={`${
                props.inputClassName ? props.inputClassName : 'p-4'
              }`}
              disabled={props.disabled}
              pattern={props.numbersOnly ? '[0-9]*' : null}
              // mask={props.mask}
              // onChangeText={(text, rawText) => {
              //     if (props.onChangeText) {
              //         props.onChangeText(text, rawText);
              //     }
              // }}
              onBlur={onBlur}
              onFocus={onFocus}
              onPaste={onPaste}
              style={{
                ...styles.inputField,
                ...textAlign,
                ...props.inputStyle,
              }}
              placeholder={props.placeholder}
              // placeholderTextColor={"#c8c8c8"}
              type={props.type}
              autoComplete={'off'}
              value={props.value}
              // secureTextEntry={props.secure}
              // textContentType={
              //     props.secure ? "oneTimeCode" : null
              // }
              onChange={(event) => {
                let input = event.target.value;
                if (props.numbersOnly) {
                  input = input.replace(/\D/g, '');
                }

                props.onChangeText ? props.onChangeText(input) : null;
                props.onChangeEvent ? props.onChangeEvent(event) : null;
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  props.onSubmit == null ? null : props.onSubmit();
                }
              }}
              // autoCapitalize="none"
              // autoCorrect={false}
              defaultValue={props.defaultValue}
              // editable={!props.disabled}
              // selectTextOnFocus={!props.disabled}
              maxLength={props.maxLength}
              max={props.maxLength}
              {...props.register}
            />
          ) : (
            <input
              ref={ref}
              name={props.name ? props.name : null}
              className={`${
                props.inputClassName ? props.inputClassName : 'px-4 py-3'
              }`}
              disabled={props.disabled}
              pattern={props.numbersOnly ? '[0-9]*' : null}
              onBlur={onBlur}
              onFocus={onFocus}
              onPaste={onPaste}
              style={{
                ...styles.inputField,
                ...textAlign,
                ...props.inputStyle,
                // ...(props.autoResize ? { resize: 'none', fieldSizing: 'content' } : {})
              }}
              placeholder={props.placeholder}
              // placeholderTextColor={"#c8c8c8"}
              // returnKeyType={props.returnKeyType}
              type={props.type}
              value={props.value}
              autoComplete={'off'}
              // secureTextEntry={props.secure}
              // textContentType={
              //     props.secure ? "oneTimeCode" : null
              // }
              onChange={(event) => {
                let input = event.target.value;
                if (props.numbersOnly) {
                  input = input.replace(/\D/g, '');
                }
                props.onChangeText ? props.onChangeText(input) : null;
                props.onChangeEvent ? props.onChangeEvent(event) : null;
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  props.onSubmit == null ? null : props.onSubmit();
                }
              }}
              // keyboardType={props.keyboardType}
              // autoCapitalize="none"
              // autoCorrect={false}
              defaultValue={props.defaultValue}
              // editable={!props.disabled}
              // selectTextOnFocus={!props.disabled}
              maxLength={props.maxLength}
              max={props.maxLength}
              {...props.register}
            />
          )}

          {renderUnit()}
          {submitBtn()}

          {props.icon?.position === 'centerRight' ? renderIcons() : null}
          {props.icon?.position === 'right' ? renderIcons() : null}
          {props.button?.position === 'right' ? renderButtons() : null}
        </div>
        {props.calendar ? props.calendar() : null}
      </div>
      {renderDescription()}
    </div>
  );
});

export default TextInputField;
